
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';

// Lazy-loaded route components
const HomePage = lazy(() => import('./pages/home/HomePage'));
const AboutPage = lazy(() => import('./pages/home/landing/AboutPage'));
const AllReviewsPage = lazy(() => import('./pages/home/landing/allReviewsPage'));
const AllCoursesPage = lazy(() => import('./pages/home/landing/allCoursesPage'));
const AllSuccessPage = lazy(() => import('./pages/home/landing/allSuccessPage'));
const PolicyPage = lazy(() => import('./pages/home/landing/PolicyPage'));
const DemoLoginPage = lazy(() => import('./pages/home/DemoLoginPage'));
const NaverCallback = lazy(() => import('./pages/teacher/settings/NaverCallback'));
const ThreadsCallback = lazy(() => import('./pages/teacher/settings/ThreadsCallback'));
const YoutubeCallback = lazy(() => import('./pages/super/YoutubeCallback'));
const TeacherPlayerPage = lazy(() => import('./pages/player/teacherPlayer/TeacherPlayerPage'));
const SharedViewerPage = lazy(() => import('./pages/sheetviewer/SharedViewerPage'));
const PublicPrintOrderPage = lazy(() => import('./pages/printOrder/PublicPrintOrderPage'));
const VocaPlayerPage = lazy(() => import('./pages/player/vocaPlayer/VocaPlayerPage'));
const SentencePlayerPage = lazy(() => import('./pages/player/sentencePlayer/SentencePlayerPage'));
const ParagraphPlayerPage = lazy(() => import('./pages/player/paragraphPlayer/ParagraphPlayerPage'));
const QuestionPlayerPage = lazy(() => import('./pages/player/questionPlayer/QuestionPlayerPage'));
const KoQuestionPlayerPage = lazy(() => import('./pages/player/koQuestionPlayer/KoQuestionPlayerPage'));
const CramPlayerPage = lazy(() => import('./pages/player/cramPlayer/CramPlayerPage'));
const BookPreviewPage = lazy(() => import('./pages/teacher/myHome/BookPreviewPage'));
const TeacherHomeRoot = lazy(() => import('./pages/teacher/myHome/TeacherHomeRoot'));
const PastExamMultiComparePage = lazy(() => import('./pages/pastExam/multiCompare'));
const PastExamComparePage = lazy(() => import('./pages/pastExam/PastExamComparePage'));
const StudentViewPage = lazy(() => import('./pages/academy/students/StudentViewPage'));
const TeacherViewPage = lazy(() => import('./pages/teacher/TeacherViewPage'));
const CompanionCamPage = lazy(() => import('./pages/player/companionCam/CompanionCamPage'));
const AttendanceCheckPage = lazy(() => import('./pages/attendance/AttendanceCheckPage'));

const PageLoadingFallback = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900 text-white gap-3">
    <Loader2 className="w-8 h-8 text-[#5e17eb] animate-spin" />
    <span className="text-xs text-slate-400 font-medium">화면을 불러오는 중...</span>
  </div>
);

const AppRoutes: React.FC = () => {
  // Simple routing logic
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  // Check for about page or instructors
  if (currentPath === '/about' || currentPath === '/instructors' || currentPath === '/teachers') {
    return <AboutPage />;
  }

  // Check for reviews page
  if (currentPath === '/reviews') {
    return <AllReviewsPage />;
  }

  // Check for course guide page
  if (currentPath === '/course-guide') {
    return <AllCoursesPage />;
  }

  // Check for success stories (실적관리) page
  if (currentPath === '/success' || currentPath === '/success/' || currentPath === '/achievements' || currentPath === '/records') {
    return <AllSuccessPage />;
  }

  // Check for policy / privacy / terms routes
  if (
    currentPath === '/policy' || currentPath === '/policy/' ||
    currentPath === '/privacy' || currentPath === '/privacy/' ||
    currentPath === '/terms' || currentPath === '/terms/'
  ) {
    return <PolicyPage />;
  }

  // Check for reviewer demo portal route (/demo)
  if (currentPath === '/demo' || currentPath === '/demo/') {
    return <DemoLoginPage />;
  }

  // Check for naver callback route
  if (currentPath === '/naver-callback') {
    return <NaverCallback />;
  }

  // Check for threads callback route
  if (currentPath === '/threads-callback') {
    return <ThreadsCallback />;
  }

  // Check for youtube callback route
  if (currentPath === '/admin/youtube-callback' || currentPath === '/admin/youtube-callback/') {
    return <YoutubeCallback />;
  }

  // Check for teacher player / lecture player route
  if (currentPath === '/teacher-player' || currentPath.startsWith('/teacher-player') || currentPath === '/lecture-player' || currentPath.startsWith('/lecture-player')) {
    return <TeacherPlayerPage />;
  }

  // Check for companion cam route (스마트폰 보조캠 연동)
  if (currentPath === '/companion-cam' || currentPath.startsWith('/companion-cam')) {
    return <CompanionCamPage />;
  }

  // Check for shared viewer route
  if (currentPath === '/viewer' || currentPath === '/viewer/') {
    return <SharedViewerPage />;
  }

  // Check for attendance route (NFC 출결 / 등하원 / 출퇴근 / 외출 / 복귀)
  if (currentPath === '/attendance' || currentPath === '/attendance/' || currentPath.startsWith('/attendance')) {
    return <AttendanceCheckPage />;
  }

  // Check for public print order route for print shops
  if (currentPath === '/print-order' || currentPath.startsWith('/print-order/')) {
    const printOrderMatch = currentPath.match(/^\/print-order\/([^/?#]+)/);
    const orderId = printOrderMatch ? printOrderMatch[1] : undefined;
    return <PublicPrintOrderPage orderIdParam={orderId} />;
  }

  // Check for voca player route
  if (currentPath === '/voca-player') {
    return <VocaPlayerPage />;
  }
  const vocaMatch = currentPath.match(/^\/voca-player\/(.+)$/);
  if (vocaMatch) {
    const setId = vocaMatch[1];
    return <VocaPlayerPage setId={setId} />;
  }

  // Check for sentence player route
  if (currentPath === '/sentence-player') {
    return <SentencePlayerPage />;
  }
  const sentenceMatch = currentPath.match(/^\/sentence-player\/(.+)$/);
  if (sentenceMatch) {
    const setId = sentenceMatch[1];
    return <SentencePlayerPage setId={setId} />;
  }

  // Check for paragraph player route
  if (currentPath === '/paragraph-player') {
    return <ParagraphPlayerPage />;
  }
  const paragraphMatch = currentPath.match(/^\/paragraph-player\/(.+)$/);
  if (paragraphMatch) {
    const setId = paragraphMatch[1];
    return <ParagraphPlayerPage setId={setId} />;
  }

  // Check for question player & OMR submit routes
  if (currentPath === '/omr/submit' || currentPath.startsWith('/omr/') || currentPath.startsWith('/omr') || currentPath === '/question-player' || currentPath.startsWith('/question-player/')) {
    const questionMatch = currentPath.match(/^\/question-player\/([^/?#]+)/);
    const bookId = questionMatch ? questionMatch[1] : undefined;
    return <QuestionPlayerPage bookId={bookId} />;
  }

  // Check for Korean question player route
  if (currentPath === '/ko-question-player' || currentPath.startsWith('/ko-question-player/')) {
    const koQuestionMatch = currentPath.match(/^\/ko-question-player\/([^/?#]+)/);
    const bookId = koQuestionMatch ? koQuestionMatch[1] : undefined;
    return <KoQuestionPlayerPage bookId={bookId} />;
  }

  // Check for Cram (서술형대비) player route
  if (currentPath === '/cram-player' || currentPath.startsWith('/cram-player/') || currentPath === '/player/cram' || currentPath.startsWith('/player/cram/')) {
    const cramMatch = currentPath.match(/^\/(?:cram-player|player\/cram)\/([^/?#]+)/);
    const paramId = cramMatch ? cramMatch[1] : undefined;
    return <CramPlayerPage paragraphId={paramId} />;
  }

  // Check for book preview route
  const bookPreviewMatch = currentPath.match(/^\/book-preview\/([^/?#]+)/);
  if (bookPreviewMatch) {
    const bookId = bookPreviewMatch[1];
    return <BookPreviewPage bookId={bookId} />;
  }

  // Check for teacher/myHome route
  const myHomeTeacherMatch = currentPath.match(/^\/teacher\/myHome\/(.+)$/);
  if (myHomeTeacherMatch) {
    const teacherId = myHomeTeacherMatch[1];
    return <TeacherHomeRoot teacherId={teacherId} />;
  }

  // Check for legacy public teacher profile route with /p/
  const legacyPublicTeacherMatch = currentPath.match(/^\/p\/(.+)$/);
  if (legacyPublicTeacherMatch) {
    const teacherId = legacyPublicTeacherMatch[1];
    return <TeacherHomeRoot teacherId={teacherId} />;
  }

  // Check for past-exam-multi-compare route
  if (currentPath.startsWith('/past-exam-multi-compare') || currentPath.startsWith('/past-exam-compare/multi')) {
    return <PastExamMultiComparePage />;
  }

  // Check for past-exam-compare route
  const pastExamCompareMatch = currentPath.match(/^\/past-exam-compare\/(.+)$/);
  if (pastExamCompareMatch) {
    const examId = pastExamCompareMatch[1];
    return <PastExamComparePage examId={examId} />;
  }

  // Check for student-view route

  const studentViewMatch = currentPath.match(/^\/student-view\/(.+)$/);
  if (studentViewMatch) {
    const memberId = studentViewMatch[1];
    return <StudentViewPage memberId={memberId} />;
  }

  // Check for teacher-view route
  const teacherViewMatch = currentPath.match(/^\/teacher-view\/(.+)$/);
  if (teacherViewMatch) {
    const memberId = teacherViewMatch[1];
    return <TeacherViewPage memberId={memberId} />;
  }

  // Check for new root-level vanity URL route (e.g. qgen-lms.web.app/math-master)
  // This must be checked last to ensure it doesn't conflict with explicitly defined valid routes
  if (currentPath !== '/' && currentPath !== '') {
    const rootVanityMatch = currentPath.match(/^\/([^/?#]+)$/);
    if (rootVanityMatch && !['favicon.ico', 'robots.txt', 'index.html', 'registration', 'student', 'teacher', 'admin', 'preview'].includes(rootVanityMatch[1])) {
        const teacherId = rootVanityMatch[1];
        return <TeacherHomeRoot teacherId={teacherId} />;
    }
  }

  return <HomePage />;
};

const App: React.FC = () => {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <AppRoutes />
    </Suspense>
  );
};

export default App;
